import React, { useEffect, useState, useCallback } from 'react';
import { X, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * DSGVO-conform Cookie-Consent banner.
 *
 * Categories:
 *   - essential   (always on, technically necessary: Auth / Session / CSRF)
 *   - statistics  (default OFF; gates Google Analytics 4)
 *
 * Rules:
 *   - The banner is rendered ONLY when REACT_APP_GA4_MEASUREMENT_ID is set
 *     AND no consent has been stored yet. If no GA4 id is configured, no
 *     statistics cookies are ever loaded and the banner stays hidden.
 *   - "Alle akzeptieren", "Nur notwendige", "Einstellungen" are visually
 *     equivalent — no dark pattern.
 *   - The banner can be reopened via the footer link ("Cookie-Einstellungen")
 *     by dispatching the `kp:open-consent` event.
 *   - The user choice is stored in localStorage (essential cookie). Revoking
 *     is possible by reopening the banner.
 */
const STORAGE_KEY = 'kp_cookie_consent_v2';
export const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || '';

const defaultPrefs = {
  essential: true,
  statistics: false,
  ts: null,
};

export const getConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const openCookieSettings = () => {
  window.dispatchEvent(new Event('kp:open-consent'));
};

const CookieConsent = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  // Show banner only when GA4 is configured AND no decision yet.
  // Also allow manual reopening via the "kp:open-consent" event.
  useEffect(() => {
    const existing = getConsent();
    if (GA4_ID && !existing) {
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      const existing = getConsent();
      if (existing) setPrefs({ ...defaultPrefs, ...existing });
      setShowSettings(false);
      setOpen(true);
    };
    window.addEventListener('kp:open-consent', handler);
    return () => window.removeEventListener('kp:open-consent', handler);
  }, []);

  const save = useCallback((next) => {
    const payload = { ...next, essential: true, ts: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    setOpen(false);
    setShowSettings(false);
    window.dispatchEvent(new CustomEvent('kp:consent', { detail: payload }));
  }, []);

  const acceptAll = () => save({ statistics: true });
  const denyAll = () => save({ statistics: false });
  const saveCustom = () => save({ statistics: !!prefs.statistics });

  if (!open) return null;

  // Three equal buttons (same visual weight, same size) — no dark pattern.
  const btnBase =
    'flex-1 sm:flex-none min-w-[160px] text-center text-xs tracking-wider px-5 py-2.5 rounded-sm border transition-colors';
  const btnPrimary = `${btnBase} bg-[#1B3A5C] border-[#1B3A5C] text-white hover:bg-[#13283f]`;
  const btnNeutral = `${btnBase} bg-white border-[#1B3A5C] text-[#1B3A5C] hover:bg-[#f0f4f8]`;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6"
      role="dialog"
      aria-modal="false"
      aria-live="polite"
      aria-label={t('cookie.title')}
    >
      <div className="mx-auto max-w-4xl bg-white border-t-4 border-[#b8893d] shadow-2xl rounded-sm overflow-hidden">
        <div className="px-6 py-6 sm:px-8 sm:py-7">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex w-11 h-11 rounded-full bg-[#fbf7f0] items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5 text-[#b8893d]" />
            </div>
            <div className="flex-1">
              <h2 className="font-serif text-lg text-[#1B3A5C] mb-2">
                {t('cookie.title')}
              </h2>
              <p className="text-sm text-[#34536b] leading-relaxed">
                {t('cookie.body')}{' '}
                <a href="/datenschutz" className="text-[#b8893d] underline">{t('cookie.privacy_link')}</a>
              </p>

              {showSettings && (
                <div className="mt-5 space-y-3 border-t border-gray-100 pt-4">
                  <label className="flex items-start gap-3 cursor-not-allowed opacity-90">
                    <input
                      type="checkbox"
                      checked
                      disabled
                      className="mt-1 w-4 h-4 accent-[#b8893d]"
                      data-testid="consent-toggle-essential"
                    />
                    <div>
                      <div className="text-[#1B3A5C] text-sm font-medium">{t('cookie.essential')}</div>
                      <div className="text-[#34536b] text-xs leading-relaxed">
                        {t('cookie.essential_desc')}
                      </div>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!prefs.statistics}
                      onChange={(e) => setPrefs((p) => ({ ...p, statistics: e.target.checked }))}
                      className="mt-1 w-4 h-4 accent-[#b8893d]"
                      data-testid="consent-toggle-statistics"
                    />
                    <div>
                      <div className="text-[#1B3A5C] text-sm font-medium">{t('cookie.analytics')}</div>
                      <div className="text-[#34536b] text-xs leading-relaxed">
                        {t('cookie.analytics_desc')}
                      </div>
                    </div>
                  </label>
                </div>
              )}

              <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 mt-5">
                {!showSettings && (
                  <>
                    <button
                      type="button"
                      onClick={acceptAll}
                      data-testid="consent-accept-all-btn"
                      className={btnPrimary}
                    >
                      {t('cookie.accept_all').toUpperCase()}
                    </button>
                    <button
                      type="button"
                      onClick={denyAll}
                      data-testid="consent-deny-all-btn"
                      className={btnNeutral}
                    >
                      {t('cookie.deny_all').toUpperCase()}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowSettings(true)}
                      data-testid="consent-customize-btn"
                      className={btnNeutral}
                    >
                      {t('cookie.customize').toUpperCase()}
                    </button>
                  </>
                )}
                {showSettings && (
                  <>
                    <button
                      type="button"
                      onClick={saveCustom}
                      data-testid="consent-save-btn"
                      className={btnPrimary}
                    >
                      {t('cookie.save').toUpperCase()}
                    </button>
                    <button
                      type="button"
                      onClick={acceptAll}
                      data-testid="consent-accept-all-btn"
                      className={btnNeutral}
                    >
                      ALLE AKZEPTIEREN
                    </button>
                    <button
                      type="button"
                      onClick={denyAll}
                      data-testid="consent-deny-all-btn"
                      className={btnNeutral}
                    >
                      NUR NOTWENDIGE
                    </button>
                  </>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={denyAll}
              aria-label="Banner schließen (nur notwendige Cookies)"
              className="text-[#1B3A5C]/40 hover:text-[#1B3A5C] flex-shrink-0"
              data-testid="consent-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
